/* eslint-disable max-lines */
import type { Ref, HTMLAttributes, ReactNode } from 'react';

import type {
    ExtendedMaterial,
    ExtendedMaterialOnTheFly,
    Material,
    MaterialOnTheFly,
} from '../../gql';
import type { IconButtonProps } from '@mui/material/IconButton';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';

import { PlusIcon } from '@bestseller-bit/frontend-community.theme.buying-theme';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import {
    getMaterialOnTheFlySearchableText,
    isExtendedMaterial,
    isExtendedMaterialOnTheFly,
    isMaterial,
    isMaterialOnTheFly,
} from '../../utils';

import MenuItemMaterial from './MenuItemMaterial';
import MenuItemMaterialOnTheFly from './MenuItemMaterialOnTheFly';
import MenuItemSkeleton from './MenuItemSkeleton';

/**
 * This type is for the [+ Material on the fly] button,
 * so we render it as an option in the autocomplete.
 * @param nonValueOptionId - This is a unique identifier for the non-value option
 * @param label - This is the label that will be displayed in the autocomplete
 * @param onClick - This is the callback function that will be called when the option is clicked
 */
interface NonValueOption {
    nonValueOptionId: string;
    label: string;
    onClick?: () => void;
}

/**
 * This type is used to represent a subcategory that has no materials.
 * It is added to the options list as a fake/filler option so that the Autocomplete
 * renders the group header for this subcategory. The option itself renders nothing,
 * and the group component shows "No materials found" when all children are invisible.
 */
interface EmptySubcategoryPlaceholder {
    emptySubcategoryPlaceholderId: string;
    materialSubcategoryName: string;
}

/**
 * This type is used to represent a material subcategory whose materials are currently collapsed.
 * It is added to the options list as a single fake/filler option so that the Autocomplete
 * renders the group header for this subcategory, while its actual materials are hidden.
 * The option itself renders nothing, and the group component shows the collapsed chevron.
 */
interface CollapsedSubcategoryPlaceholder {
    collapsedSubcategoryPlaceholderId: string;
    materialSubcategoryName: string;
}

type ValueType =
    | CollapsedSubcategoryPlaceholder
    | EmptySubcategoryPlaceholder
    | ExtendedMaterial
    | ExtendedMaterialOnTheFly
    | Material
    | MaterialOnTheFly
    | NonValueOption
    | undefined; // The value can be undefined when empty, a material on the fly or a material from either extended composition or bom

type MultipleType = false; // We can never select multiple materials in a field, so this is false
type DisableClearableType = false; // The design defaults to a clear button
type FreeSoloType = false; // We do not allow free text input, the users must select a real value from the list

const mofGroupName = 'Material on the fly';

/**
 * The autocomplete only displays groups with actual materials
 * However, we want the material on the fly group to always display (if it's allowed), also when there is no custom materials
 * Therefore we add a filler material on the fly into that group, and we hide it in the dropdown
 */
const fillerMaterialOnTheFly: ExtendedMaterialOnTheFly | MaterialOnTheFly = {
    materialOnTheFlyPublicId: 'filler-material-on-the-fly-public-id',
    materialSubcategory: {
        materialSubcategoryPublicId: 'filler-material-subcategory-public-id',
        materialSubcategoryName: 'filler-material-subcategory-name',
        isDeleted: false,
    },
    compositions: [],
    manufactureDetail: {
        isDeleted: true,
        manufactureDetailName: 'filler-manufacture-detail-name',
        manufactureDetailPublicId: 'filler-manufacture-detail-public-id',
    },
};

/**
 * This function is provided as-is to the MaterialAutocomplete, and holds all logic for grouping the options
 * @param option - The current option being processed
 * @param shouldGroupByMaterialSubcategory - Whether to group by the material's subcategory or not
 * @returns A string that represents the group name for the option
 */
const groupByFn = ({
    option,
    shouldGroupByMaterialSubcategory = true,
}: {
    option: ValueType;
    shouldGroupByMaterialSubcategory?: boolean;
}): string => {
    // If the option is not defined or a non-value option, it's grouped with an empty group name string.
    if (!option || 'nonValueOptionId' in option) return '';

    // If the option is an empty subcategory placeholder, we group it under its subcategory name
    if ('emptySubcategoryPlaceholderId' in option)
        return option.materialSubcategoryName;

    // If the option is a collapsed subcategory placeholder, we group it under its subcategory name
    if ('collapsedSubcategoryPlaceholderId' in option)
        return option.materialSubcategoryName;

    // If the option is a MOF, we group it under the MOF group name
    const isMof =
        isMaterialOnTheFly(option) || isExtendedMaterialOnTheFly(option);
    if (isMof) return mofGroupName;

    /**
     * If it's a regular material,
     * we group it under the material library group name or the material subcategory name,
     * depending on the function's argument
     */
    return shouldGroupByMaterialSubcategory
        ? (option.materialSubcategory.materialSubcategoryName ?? 'Unknown')
        : 'Material library';
};

/**
 * This function is used to render each option in the MaterialAutocomplete dropdown
 * It handles the rendering of materials, materials on the fly, or interactive buttons
 * @param props - The props that are passed to the option, including any HTML attributes and a ref
 * @param option - The current option being rendered
 * @param isStreaming - Whether material images are still streaming in
 * @param focusedOptionKey - The key of the currently focused option, used to highlight MOFs
 * @param handleEditClick - Callback fired when a MOF's edit button is clicked
 * @returns A ReactNode that represents the rendered option
 */
const renderOptionFn = ({
    props,
    option,
    isStreaming,
    focusedOptionKey,
    searchQuery,
    onEditClick: handleEditClick,
}: {
    props: HTMLAttributes<HTMLLIElement> & { ref?: Ref<HTMLLIElement> };
    option: ValueType;
    isStreaming: boolean;
    focusedOptionKey?: string | null;
    searchQuery?: string;
    onEditClick?: IconButtonProps['onClick'];
}): ReactNode => {
    // If the material is undefined, we return a skeleton to indicate loading
    if (!option) return <MenuItemSkeleton />;

    // If the option is an empty subcategory placeholder, we render nothing.
    // The group component will show "No materials found" when all children are invisible.
    if ('emptySubcategoryPlaceholderId' in option) return;

    // If the option is a collapsed subcategory placeholder, we render nothing.
    // The group component only renders the (collapsed) header for this subcategory.
    if ('collapsedSubcategoryPlaceholderId' in option) return;

    // If the option is a non-value option, we render it as an interactive button
    if ('nonValueOptionId' in option) {
        return (
            <li
                {...props}
                key={option.nonValueOptionId}
                data-mof-option="true"
                style={{
                    ...props.style,
                    width: '100%',
                    gap: 8,
                }}
            >
                <ListItemIcon
                    sx={{
                        minWidth: 16,
                        color: ({ palette }) => palette.primary.main,
                    }}
                >
                    <PlusIcon size={16} />
                </ListItemIcon>
                <ListItemText
                    primary={option.label}
                    slotProps={{
                        primary: {
                            variant: 'buttons.small',
                            color: 'text.primary',
                        },
                    }}
                />
            </li>
        );
    }

    // If the option is a material on the fly, we render it as a MOF
    if (isMaterialOnTheFly(option) || isExtendedMaterialOnTheFly(option)) {
        if (
            option.materialOnTheFlyPublicId ===
            fillerMaterialOnTheFly.materialOnTheFlyPublicId
        ) {
            return;
        }

        const currentOptionKey = generateOptionKey(option);
        const isFocused = focusedOptionKey === currentOptionKey;
        return (
            <MenuItemMaterialOnTheFly
                {...props}
                key={option.materialOnTheFlyPublicId}
                mof={option}
                isFocused={isFocused}
                onEditClick={handleEditClick}
                highlight={searchQuery}
            />
        );
    }

    // If the option is a regular material, we render it as a regular material
    return (
        <MenuItemMaterial
            {...props}
            key={`${option.libraryRef}-${option.materialAttachment?.format}`} // Adding attachment to the key forces the menu item to re-render when attachment changes
            material={option}
            isImageLoading={isStreaming}
            highlight={searchQuery}
        />
    );
};

/**
 * This function is used to compare the current value with the option in the autocomplete.
 * The Autocomplete uses this function to determine which option is selected, and applies the correct styles.
 * @param option - The option that is being compared to the current value
 * @param currentValue - The current value that is selected in the autocomplete
 * @returns A boolean indicating whether the option is equal to the current value
 */
const isOptionEqualToValueFn = (
    option: ValueType,
    currentValue: ValueType
): boolean => {
    // If option and/or currentValue are undefined or non-value, they can never be equal to any option
    if (
        !option ||
        !currentValue ||
        'nonValueOptionId' in option ||
        'nonValueOptionId' in currentValue ||
        'emptySubcategoryPlaceholderId' in option ||
        'emptySubcategoryPlaceholderId' in currentValue ||
        'collapsedSubcategoryPlaceholderId' in option ||
        'collapsedSubcategoryPlaceholderId' in currentValue
    ) {
        return false;
    }

    // If both option and currentValue are MOFs, we compare their public IDs to see if they are equal
    if (
        (isMaterialOnTheFly(option) && isMaterialOnTheFly(currentValue)) ||
        (isExtendedMaterialOnTheFly(option) &&
            isExtendedMaterialOnTheFly(currentValue))
    ) {
        return (
            option.materialOnTheFlyPublicId ===
            currentValue.materialOnTheFlyPublicId
        );
    }

    // If both option and currentValue are regular materials, we compare their public IDs to see if they are equal
    if (
        (isMaterial(option) && isMaterial(currentValue)) ||
        (isExtendedMaterial(option) && isExtendedMaterial(currentValue))
    ) {
        return option.materialPublicId === currentValue.materialPublicId;
    }

    // If one option is a MOF and the other is a regular material, they can never be equal.
    return false;
};

/**
 * This function generates a unique key for each option in the autocomplete.
 * @param option - The option for which we want to generate a key
 * @returns A string that represents the unique key for the option
 */
const generateOptionKey = (option: ValueType): string => {
    // If the option is undefined, we generate a new random UUID
    if (!option) return crypto.randomUUID();

    // If the option is a non-value option, we return its nonValueOptionId as key
    if ('nonValueOptionId' in option) return option.nonValueOptionId;

    // If the option is an empty subcategory placeholder, we return its id as key
    if ('emptySubcategoryPlaceholderId' in option)
        return option.emptySubcategoryPlaceholderId;

    // If the option is a collapsed subcategory placeholder, we return its id as key
    if ('collapsedSubcategoryPlaceholderId' in option)
        return option.collapsedSubcategoryPlaceholderId;

    // If the option is a MOF, we return its materialOnTheFlyPublicId as key
    if (isMaterialOnTheFly(option) || isExtendedMaterialOnTheFly(option)) {
        return option.materialOnTheFlyPublicId;
    }

    // If the option is a regular material, we return its materialPublicId as key
    return option.materialPublicId;
};

/**
 * This function sorts materials by their material subcategory name.
 * @param a - The first material to compare
 * @param b - The second material to compare
 * @returns A number indicating the sort order
 */
const sortMaterialsByMaterialSubcategoryName = (
    a: ExtendedMaterial | Material,
    b: ExtendedMaterial | Material
): number => {
    // If they are from the same subcategory, we sort based on library ref
    if (
        a.materialSubcategory.materialSubcategoryName ===
        b.materialSubcategory.materialSubcategoryName
    ) {
        return a.libraryRef.localeCompare(b.libraryRef);
    }

    // If they are not from the same subcategory, we sort based on subcategory name
    return a.materialSubcategory.materialSubcategoryName.localeCompare(
        b.materialSubcategory.materialSubcategoryName
    );
};

/**
 * This function filters the options based on the input value and whether the materials are grouped by subcategory.
 * @param unfilteredOptions - The list of options that are not yet filtered
 * @param state - The current state of the autocomplete, which includes the input value
 * @param isGroupedByMaterialSubcategory - Whether the materials are grouped by subcategory
 * @returns An array of options that match the input value
 */
const filterOptionsFn = ({
    unfilteredOptions,
    state,
    isGroupedByMaterialSubcategory,
}: {
    unfilteredOptions: Array<ValueType>;
    state: FilterOptionsState<ValueType>;
    isGroupedByMaterialSubcategory: boolean;
}): Array<ValueType> => {
    // Without an input value, we return all unfiltered options
    if (!state.inputValue) return unfilteredOptions;

    return unfilteredOptions.filter((option): boolean => {
        if (!option) return true;

        // Always show the button
        if ('nonValueOptionId' in option) return true;

        // Hide empty subcategory placeholders during search — groups without matches should not show
        if ('emptySubcategoryPlaceholderId' in option) return false;

        // Always show collapsed subcategory placeholders so the group header stays visible
        if ('collapsedSubcategoryPlaceholderId' in option) return true;

        // First we check if the option's subcategory matches, and keep those materials in
        if (
            isGroupedByMaterialSubcategory &&
            option.materialSubcategory.materialSubcategoryName
                .toLowerCase()
                .includes(state.inputValue.toLowerCase())
        ) {
            return true;
        }

        // MOFs are filtered on the frontend by matching any text that is rendered
        // for them (subcategory header, title, and description).
        if (isMaterialOnTheFly(option) || isExtendedMaterialOnTheFly(option)) {
            // The filler MOF renders nothing, so keep it out of search results.
            if (
                option.materialOnTheFlyPublicId ===
                fillerMaterialOnTheFly.materialOnTheFlyPublicId
            ) {
                return false;
            }

            return getMaterialOnTheFlySearchableText(option).includes(
                state.inputValue.toLowerCase()
            );
        }

        // We do not filter out any materials, they are filtered by the backend
        if (isMaterial(option) || isExtendedMaterial(option)) {
            return true;
        }

        return false;
    });
};

/** Checks whether a given option will render a visible DOM element in the dropdown. */
const isRenderableOption = (option: ValueType): boolean => {
    if (!option) return true;
    if ('emptySubcategoryPlaceholderId' in option) return false;
    if ('collapsedSubcategoryPlaceholderId' in option) return false;
    if ('nonValueOptionId' in option) return true;
    if (
        (isMaterialOnTheFly(option) || isExtendedMaterialOnTheFly(option)) &&
        option.materialOnTheFlyPublicId ===
            fillerMaterialOnTheFly.materialOnTheFlyPublicId
    ) {
        return false;
    }
    return true;
};

/**
 * Determines whether an option should be disabled (non-interactive).
 * Placeholders and the filler render nothing, so disabling them keeps keyboard
 * navigation from landing on invisible items and prevents them from being selected.
 */
const getOptionDisabledFn = (option: ValueType): boolean => {
    if (!option) return false; // Loading skeletons stay enabled
    if ('nonValueOptionId' in option) return false;
    if ('emptySubcategoryPlaceholderId' in option) return true;
    if ('collapsedSubcategoryPlaceholderId' in option) return true;
    if (
        (isMaterialOnTheFly(option) || isExtendedMaterialOnTheFly(option)) &&
        option.materialOnTheFlyPublicId ===
            fillerMaterialOnTheFly.materialOnTheFlyPublicId
    ) {
        return true;
    }
    return false;
};

export {
    generateOptionKey,
    getOptionDisabledFn,
    isRenderableOption,
    sortMaterialsByMaterialSubcategoryName,
    filterOptionsFn,
    fillerMaterialOnTheFly,
    groupByFn,
    isOptionEqualToValueFn,
    mofGroupName,
    renderOptionFn,
};
export type {
    CollapsedSubcategoryPlaceholder,
    DisableClearableType,
    EmptySubcategoryPlaceholder,
    FreeSoloType,
    MultipleType,
    ValueType,
    NonValueOption,
};
